import { Form, type FormInstance } from 'antd';
import { type FC, useMemo } from 'react';
import type { Accessors } from './FormSelectBox';

interface OptionType {
  [key: string]: string | number;
}

interface FormNativeSelectBoxProps {
  form: FormInstance;
  name: string;
  options: OptionType[];
  accessors: Accessors;
  label?: string;
  disableValidation?: boolean;
  isRequired?: boolean;
  labelClass?: string;
  editMode?: boolean;
  onChange?: (value: string) => void;
  tabIndex?: number;
  dataTestId?: string;
}

const FormNativeSelectBox: FC<FormNativeSelectBoxProps> = ({
  form,
  name,
  options = [],
  accessors,
  label,
  disableValidation,
  isRequired,
  labelClass,
  editMode,
  onChange,
  tabIndex = 0,
  dataTestId = '',
}) => {
  const value = Form.useWatch(name, form);
  const fieldError = form.getFieldError(name);
  const hasError = useMemo(() => Boolean(fieldError?.length > 0), [fieldError]);

  return (
    <div className="native-select-box-container">
      {label && (
        <label htmlFor={name} className={`native-label ${labelClass}`}>
          {label}
          {isRequired && !disableValidation && <span className="required">*</span>}
        </label>
      )}
      <select
        id={name}
        value={value}
        className={`native-select-box ${hasError ? 'input-error' : ''} ${editMode ? 'edit-mode' : ''}`}
        onChange={(e) => {
          form.setFieldValue(name, e.target.value);
          if (onChange) {
            onChange(e.target.value);
          }
        }}
        disabled={!editMode}
        tabIndex={tabIndex}
        data-testid={dataTestId}
      >
        <option value="">Select</option>
        {options.map((row) => (
          <option value={row[accessors.value]} key={row[accessors.value]}>
            {row[accessors.label]}
          </option>
        ))}
      </select>
    </div>
  );
};

export default FormNativeSelectBox;
